import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Breadcrumbs from "@/components/Breadcrumbs";
import { Clock, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { lazy, Suspense } from "react";
const FloatingTestCTA = lazy(() => import("@/components/FloatingTestCTA"));
import tutorialIphoneImage from "@/assets/installation-iptv-iphone-ipad.jpg";
import tutorialAndroidImage from "@/assets/installation-iptv-android.jpg";
import tutorialFirestickImage from "@/assets/installation-iptv-firestick-amazon.webp";
import tutorialSmartTvAndroidImage from "@/assets/installation-iptv-smart-tv-samsung-lg.jpg";
import tutorialWindowsImage from "@/assets/installation-iptv-windows-pc.jpg";
import tutorialMacImage from "@/assets/installation-iptv-mac-apple.jpg";
import { useAffiliateTracking } from "@/hooks/useAffiliateTracking";

const Tutoriels = () => {
  useAffiliateTracking();
  const tutorials = [
    {
      title: "Tutoriel IPTV – iPhone / iPad",
      slug: "/tutoriels/iptv-iphone-ipad",
      description: "Installez l'IPTV sur votre iPhone ou iPad en quelques minutes avec Smarters Player Lite.",
      duration: "2 min",
      image: tutorialIphoneImage,
      alt: "Installation IPTV sur iPhone et iPad avec Smarters Player Lite"
    },
    {
      title: "Tutoriel IPTV – Android",
      slug: "/tutoriels/iptv-android",
      description: "Installez l'IPTV sur votre téléphone ou tablette Android avec IPTV Smarters Pro.",
      duration: "2 min",
      image: tutorialAndroidImage,
      alt: "Installation IPTV sur smartphone et tablette Android"
    },
    {
      title: "Tutoriel IPTV – Firestick",
      slug: "/tutoriels/iptv-firestick",
      description: "Installez l'IPTV sur votre Amazon Fire Stick avec IPTV Smarters Pro.",
      duration: "5 min",
      image: tutorialFirestickImage,
      alt: "Installation IPTV sur Amazon Fire TV Stick"
    },
    {
      title: "Tutoriel IPTV – Smart TV Android",
      slug: "/tutoriels/iptv-smart-tv-android",
      description: "Installez l'IPTV sur Samsung, LG, Hisense, Sony avec IPTV Smarters Pro.",
      duration: "5 min",
      image: tutorialSmartTvAndroidImage,
      alt: "Installation IPTV sur Smart TV Samsung, LG, Sony, Hisense"
    },
    {
      title: "Tutoriel IPTV – Windows",
      slug: "/tutoriels/iptv-windows",
      description: "Installez l'IPTV sur votre PC Windows avec IPTV Smarters Pro.",
      duration: "2 min",
      image: tutorialWindowsImage,
      alt: "Installation IPTV sur ordinateur PC Windows"
    },
    {
      title: "Tutoriel IPTV – Mac",
      slug: "/tutoriels/iptv-mac",
      description: "Installez l'IPTV sur votre Mac avec Smarters Player Lite.",
      duration: "2 min",
      image: tutorialMacImage,
      alt: "Installation IPTV sur MacBook et iMac Apple"
    }
  ];

  return (
    <>
      <Helmet>
        <title>Tutoriels IPTV - Guides d'installation | RoiDuStreaming</title>
        <meta name="description" content="Découvrez nos tutoriels pour installer et configurer l'IPTV sur tous vos appareils : iPhone, iPad, Android, Smart TV, Fire Stick et plus encore." />
        <meta name="keywords" content="tutoriel IPTV, guide installation IPTV, configurer IPTV, IPTV iPhone, IPTV Android" />
        <link rel="canonical" href="https://roidustreaming.fr/tutoriels" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        <Breadcrumbs />
        
        {/* Hero Section */}
        <section className="relative min-h-[40vh] flex items-center justify-center px-4 py-20 hero-bg">
          <div className="absolute inset-0 bg-gradient-to-b from-background/50 to-background"></div>
          <div className="relative z-10 max-w-5xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6 gradient-text">
              Tutoriels IPTV
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground">
              Guides d'installation pour tous vos appareils
            </p>
          </div>
        </section>

        {/* Tutorials List */}
        <section className="py-20 px-4">
          <div className="max-w-5xl mx-auto space-y-6">
            {tutorials.map((tutorial, index) => (
              <Link 
                key={index} 
                to={tutorial.slug} 
                className="block group"
                aria-label={`Voir le tutoriel ${tutorial.title}`}
              >
                <article className="card-premium flex flex-col md:flex-row overflow-hidden hover:scale-[1.02] transition-all duration-300">
                  {/* Image */}
                  <div className="md:w-64 lg:w-80 h-48 md:h-auto flex-shrink-0">
                    <img 
                      src={tutorial.image} 
                      alt={tutorial.alt}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 p-6 md:p-8 flex flex-col justify-center">
                    <div className="flex items-center gap-2 text-muted-foreground text-sm mb-3">
                      <Clock className="w-4 h-4" />
                      <span>Installation en {tutorial.duration}</span>
                    </div>
                    
                    <h2 className="text-2xl md:text-3xl font-bold gradient-text mb-3 group-hover:opacity-80 transition-opacity">
                      {tutorial.title}
                    </h2>
                    
                    <p className="text-foreground/80 text-lg mb-4">
                      {tutorial.description}
                    </p>
                    
                    <div className="flex items-center gap-2 text-primary font-medium">
                      <span>Voir le tutoriel</span>
                      <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </article>
              </Link>
            ))}
          </div>
        </section>

        <Footer />
        
        <Suspense fallback={null}>
          <FloatingTestCTA />
        </Suspense>
      </div>
    </>
  );
};

export default Tutoriels;
